import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { container } from '@/app/container';
import { TotvsRepositoryToken } from '../../di/tokens';
import type {
  ListTotvsSyncTasksInput,
  ProcessTotvsSyncTaskNowInput,
  ReprocessBatchTotvsSyncsInput,
  ReprocessTotvsSyncTaskInput,
  UpdateTotvsSyncTaskPayloadInput,
} from '../../domain/entities/totvs.entity';

const SYNC_TASKS_QUERY_KEY = ['totvs', 'sync-tasks'];

export function useTotvsSyncTasks(input: ListTotvsSyncTasksInput) {
  const repository = container.resolve(TotvsRepositoryToken);
  return useQuery({
    queryKey: [
      ...SYNC_TASKS_QUERY_KEY,
      input.page ?? 1,
      input.perPage ?? 10,
      input.status ?? '',
      input.productExternalId ?? '',
      input.errorCode ?? '',
      input.userId ?? '',
    ],
    queryFn: () => repository.listSyncTasks(input),
    staleTime: 1000 * 30,
    placeholderData: keepPreviousData,
  });
}

export function useTotvsSyncTask(id: string | undefined) {
  const repository = container.resolve(TotvsRepositoryToken);
  return useQuery({
    queryKey: [...SYNC_TASKS_QUERY_KEY, 'detail', id],
    queryFn: () => repository.getSyncTask(id ?? ''),
    enabled: !!id,
    staleTime: 1000 * 30,
  });
}

function useInvalidateSyncTasks() {
  const queryClient = useQueryClient();
  return (id?: string) => {
    void queryClient.invalidateQueries({ queryKey: SYNC_TASKS_QUERY_KEY });
    if (id) {
      void queryClient.invalidateQueries({
        queryKey: [...SYNC_TASKS_QUERY_KEY, 'detail', id],
      });
    }
  };
}

export function useMarkTotvsSyncTaskPending() {
  const repository = container.resolve(TotvsRepositoryToken);
  const invalidate = useInvalidateSyncTasks();
  return useMutation({
    mutationFn: (vars: { id: string; input?: ReprocessTotvsSyncTaskInput }) =>
      repository.reprocessSyncTask(vars.id, vars.input),
    onSuccess: (_, vars) => invalidate(vars.id),
  });
}

/**
 * Reprocesso em lote por filtro (§9.3): reseta para PENDING todas as tasks que
 * casam o `status`/`errorCode` informados, numa única chamada ao backend — que já
 * audita e limita por rodada. Substitui o antigo laço de N POSTs no navegador.
 */
export function useReprocessBatchTotvsSyncs() {
  const repository = container.resolve(TotvsRepositoryToken);
  const invalidate = useInvalidateSyncTasks();
  return useMutation({
    mutationFn: (input: ReprocessBatchTotvsSyncsInput) =>
      repository.reprocessBatch(input),
    onSuccess: () => invalidate(),
  });
}

export function useProcessTotvsSyncTaskNow() {
  const repository = container.resolve(TotvsRepositoryToken);
  const invalidate = useInvalidateSyncTasks();
  return useMutation({
    mutationFn: (vars: { id: string; input?: ProcessTotvsSyncTaskNowInput }) =>
      repository.processSyncTaskNow(vars.id, vars.input),
    onSuccess: (_, vars) => invalidate(vars.id),
  });
}

/**
 * Edita o snapshot ZMDSALUNO antes de reprocessar (§9.4) — corrige no front
 * casos como CPF/CNPJ trocado sem precisar de cirurgia no banco.
 */
export function useUpdateTotvsSyncTaskPayload() {
  const repository = container.resolve(TotvsRepositoryToken);
  const invalidate = useInvalidateSyncTasks();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      input: UpdateTotvsSyncTaskPayloadInput;
    }) => repository.updateSyncTaskPayload(vars.id, vars.input),
    onSuccess: (_, vars) => invalidate(vars.id),
  });
}
